'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { IconArrowLeft } from '@/templates/Heritage/utils/icons';
import { Plus, Minus, RotateCcw } from 'lucide-react';
import useKrpanoMap, { UseKrpanoMapOptions, KrpanoInterface } from './hooks/useKrpanoMap';
import { useKrpano } from '@/contexts/KrpanoProvider';
import MinimapPolygonOverlay from './MinimapPolygonOverlay';
import { MinimapPolygon } from '@/templates/Heritage/utils/services/minimap';

export interface KrpanoMapViewerProps extends UseKrpanoMapOptions {
    /** Force expanded view */
    forceExpanded?: boolean;

    /** Custom class name for the container */
    className?: string;

    /** Show back button when there's history */
    showBackButton?: boolean;

    /** Custom height for the map container */
    height?: string;

    /** Auto-initialize on mount */
    autoInitialize?: boolean;

    /** Custom no-data message */
    noDataMessage?: string;

    /** Custom error message */
    errorMessage?: string;

    /** Render function for custom loading state */
    renderLoading?: () => React.ReactNode;

    /** Render function for custom error state */
    renderError?: (error: string, onRetry: () => void) => React.ReactNode;

    /** Render function for custom no-data state */
    renderNoData?: () => React.ReactNode;

    /** Callback for double-click on the map - receives krpano instance and mouse event */
    onDoubleClick?: (krpano: KrpanoInterface | null, event: React.MouseEvent) => void;

    /** Enable polygon overlay rendering */
    showPolygons?: boolean;

    /** Callback when polygon is clicked */
    onPolygonClick?: (polygon: MinimapPolygon) => void;

    /** Show internal zoom controls */
    showZoomControls?: boolean;

    /** Expose zoom control actions to parent */
    onZoomControlsReady?: (controls: {
        zoomIn: () => void;
        zoomOut: () => void;
        resetZoom: () => void;
    } | null) => void;

    /** Optional project access token for locked minimap APIs */
    accessToken?: string;
}

/**
 * KrpanoMapViewer - A reusable component for rendering KRPano maps
 * 
 * This component can be used anywhere you need to display a minimap/map with KRPano.
 * It handles all the initialization, error states, and provides a clean API.
 * 
 * @example
 * // Basic usage
 * <KrpanoMapViewer mapId="123" />
 * 
 * @example
 * // With callbacks
 * <KrpanoMapViewer 
 *   mapId="123"
 *   onMapReady={(krpano) => console.log('Map ready!', krpano)}
 *   onMarkerClick={() => console.log('Marker clicked')}
 * />
 * 
 * @example
 * // Force expanded with custom height
 * <KrpanoMapViewer 
 *   mapId="123"
 *   forceExpanded
 *   height="80vh"
 * />
 */
export default function KrpanoMapViewer({
    mapId,
    targetElementId = 'krpano_map_pano',
    xmlUrl,
    onMarkerClick,
    onImage2DMarkerClick,
    onMapReady,
    onError,
    onLocationsLoaded,
    forceExpanded = false,
    className = '',
    showBackButton = true,
    height,
    autoInitialize = true,
    noDataMessage,
    errorMessage,
    renderLoading,
    renderError,
    renderNoData,
    customSceneClickHandler,
    customGroupClickHandler,
    onDoubleClick,
    isMapForAdmin,
    onclickMarkerInAdmin,
    enableZoomPan = true,
    showPolygons = false,
    onPolygonClick,
    showZoomControls = true,
    onZoomControlsReady,
    accessToken,
    hoveredLocationId,
    onMarkerHover,
    selectedLocationId,
    onMarkerSelect,
    currentScene: propCurrentScene,
    mapMarkerDisplay,
}: KrpanoMapViewerProps) {
    const t = useTranslations();

    const { currentScene: contextCurrentScene } = useKrpano();
    const currentScene = propCurrentScene ?? contextCurrentScene;

    // Container size for polygon overlay
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    // Touch devices don't use hover tooltips; skip mouse tracking to avoid re-renders
    const isTouchDeviceRef = useRef(
        typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches
    );

    // Update container size on resize
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const newSize = {
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight,
                };
                setContainerSize(newSize);
            }
        };

        // Initial update
        updateSize();
        
        // Also update after a short delay to catch late layout changes
        const timeoutId = setTimeout(updateSize, 500);
        
        window.addEventListener('resize', updateSize);
        return () => {
            window.removeEventListener('resize', updateSize);
            clearTimeout(timeoutId);
        };
    }, []);

    // Mouse position for tooltip placement
    const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const {
        isLoading,
        isReady,
        error,
        noMapData,
        minimapData,
        targetElementRef,
        initialize,
        goBack,
        retry,
        canGoBack,
        zoomIn,
        zoomOut,
        resetZoom,
        krpanoRef,
        mapTransform,
        hoverTooltip,
        changeMap,
    } = useKrpanoMap({

        mapId,
        targetElementId,
        xmlUrl,
        onMarkerClick,
        onImage2DMarkerClick,
        onMapReady,
        onError,
        onLocationsLoaded,
        currentScene,
        customSceneClickHandler,
        customGroupClickHandler,
        isMapForAdmin,
        onclickMarkerInAdmin,
        enableZoomPan,
        accessToken,
        hoveredLocationId,
        onMarkerHover,
        selectedLocationId,
        onMarkerSelect,
        mapMarkerDisplay,
    });

    // Handle dynamic mapId changes without remounting the entire component
    useEffect(() => {
        if (isReady && mapId && minimapData?.id && Number(mapId) !== minimapData.id) {
            changeMap(Number(mapId), true);
        }
    }, [mapId, isReady, minimapData?.id, changeMap]);

    /**
     * The exact pixel rectangle occupied by the KRPano map image inside the container.
     * Computed from layer[map] scale/x/y/imagewidth/imageheight so it stays accurate
     * at any zoom level, pan offset, or screen size.
     */
    const mapRect = useMemo(() => {
        const { scale, x, y, imageWidth, imageHeight } = mapTransform;
        if (containerSize.width === 0 || imageWidth === 0 || imageHeight === 0) return null;
        const renderedW = imageWidth * scale;
        const renderedH = imageHeight * scale;
        return {
            left: containerSize.width / 2 + x - renderedW / 2,
            top: containerSize.height / 2 + y - renderedH / 2,
            width: renderedW,
            height: renderedH,
        };
    }, [containerSize, mapTransform]);

    // Auto-initialize on mount
    const hasAutoInitializedRef = useRef(false);
    useEffect(() => {
        if (autoInitialize && !hasAutoInitializedRef.current) {
            hasAutoInitializedRef.current = true;
            initialize();
        }
    }, [autoInitialize, initialize]);

    // Expose control handlers to parent when requested
    useEffect(() => {
        if (!onZoomControlsReady) return;
        onZoomControlsReady({ zoomIn, zoomOut, resetZoom });
        return () => onZoomControlsReady(null);
    }, [onZoomControlsReady, zoomIn, zoomOut, resetZoom]);

    // Default height based on expanded state
    // const containerHeight = height || (forceExpanded ? '80vh' : '37vh');

    // Render no data state
    if (noMapData) {
        if (renderNoData) {
            return <>{renderNoData()}</>;
        }
        return (
            <div className="text-center py-8">
                <p className="text-gray-600 font-medium">
                    {noDataMessage || t('MATERIAL_DATA_MISSING')}
                </p>
            </div>
        );
    }

    return (
        <div className={`space-y-4 relative h-full w-full ${className}`}>
            {/* Map Display Container */}
            <div className="sticky top-0 z-10 shadow-sm mb-0 h-full w-full">
                {/* Back Button */}
                {showBackButton && !forceExpanded && canGoBack && minimapData && minimapData.id !== 1 && (
                    <div className="mb-4 px-4">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-all duration-200"
                        >
                            <IconArrowLeft className="h-4 w-4" />
                            Quay lại
                        </button>
                    </div>
                )}

                {/* Map Container */}
                <div
                    ref={containerRef}
                    style={{ height: height || '100%', overscrollBehavior: 'none', touchAction: 'none', WebkitOverflowScrolling: 'auto' } as React.CSSProperties}
                    className="flex items-center justify-center p-0 bg-[var(--gen-minimap-container-bg)] h-full w-full"
                >
                    <div
                        className="relative overflow-hidden h-full w-full"
                        style={{ overscrollBehavior: 'none', touchAction: 'none' }}
                        onMouseMove={isTouchDeviceRef.current ? undefined : (e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
                        }}
                        onMouseLeave={() => {
                            // tooltip hides via KRPano onout → window.__mmHide_xxx
                        }}
                    >
                        {/* KRPano Target Element */}
                        <div
                            id={targetElementId}
                            ref={targetElementRef}
                            className="w-full h-full minimap-container"
                            style={{
                                backgroundColor: 'transparent',
                                position: 'relative',
                                zIndex: 1,
                                touchAction: 'none', // Let JS handle all touch gestures (pinch-zoom, pan)
                                overscrollBehavior: 'none', // Prevent iOS Safari rubber-banding
                                WebkitUserSelect: 'none',
                                userSelect: 'none',
                            } as React.CSSProperties}
                            onDoubleClick={(e) => {
                                if (onDoubleClick) {
                                    onDoubleClick(krpanoRef.current, e);
                                }
                            }}
                        />

                        {/* Polygon Overlay — positioned/sized from KRPano image rect */}
                        {showPolygons && minimapData && mapRect && (
                            <MinimapPolygonOverlay
                                overlayId={`${targetElementId}_overlay`}
                                minimapId={minimapData.id}
                                mapRect={mapRect}
                                targetElementId={targetElementId}
                                accessToken={accessToken}
                                onPolygonClick={onPolygonClick}
                            />
                        )}

                        {/* Marker Hover Tooltip */}
                        {hoverTooltip?.visible && hoverTooltip?.text && (
                            <div
                                className="pointer-events-none absolute z-50 bg-gray-900/90 text-white text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap shadow-lg"
                                style={{ left: mousePos.x + 14, top: Math.max(mousePos.y - 36, 4) }}
                            >
                                {hoverTooltip.text}
                            </div>
                        )}

                        {/* Loading Overlay */}
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                                {renderLoading ? (
                                    renderLoading()
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-green-500 mb-3" />
                                        <p className="text-gray-600">{t('LOADING_TEXT')}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Error Overlay */}
                        {error && error !== 'timeout' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 z-10">
                                {renderError ? (
                                    renderError(error, retry)
                                ) : (
                                    <div className="text-center p-4 bg-white/90 rounded-lg shadow-lg">
                                        {error.includes('XML') || error.includes('404') ? (
                                            <>
                                                <div className="mb-4">
                                                    <svg
                                                        className="w-12 h-12 mx-auto text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1.5}
                                                            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7"
                                                        />
                                                    </svg>
                                                </div>
                                                <p className="text-gray-800 font-medium mb-2">
                                                    {errorMessage || t('MATERIAL_DATA_MISSING')}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-12 h-12 mx-auto mb-3 text-gray-600">⚠️</div>
                                                <p className="text-gray-800 font-medium mb-2">Lỗi tải bản đồ</p>
                                                <p className="text-gray-600 text-sm max-w-xs">{error}</p>
                                                <button
                                                    onClick={retry}
                                                    className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                                >
                                                    Thử lại
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Zoom Controls */}
                        {showZoomControls && !isLoading && !error && !noMapData && (
                            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2147483000 }}>
                                <div
                                    className="absolute bottom-4 right-4 flex flex-col gap-2 pointer-events-auto"
                                    onClick={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onPointerUp={(e) => e.stopPropagation()}
                                    onWheel={(e) => e.stopPropagation()}
                                >
                                    <button
                                        type="button"
                                        onClick={zoomIn}
                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                        style={{ touchAction: 'manipulation' }}
                                        title="Phóng to"
                                    >
                                        <Plus className="w-5 h-5 text-gray-700" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={zoomOut}
                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                        style={{ touchAction: 'manipulation' }}
                                        title="Thu nhỏ"
                                    >
                                        <Minus className="w-5 h-5 text-gray-700" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetZoom}
                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                        style={{ touchAction: 'manipulation' }}
                                        title="Đặt lại zoom"
                                    >
                                        <RotateCcw className="w-5 h-5 text-gray-700" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Re-export types for convenience
export type { KrpanoInterface } from './hooks/useKrpanoMap';
export { useKrpanoMap } from './hooks/useKrpanoMap';
